import React, { useState } from 'react'
import PhotoLightbox from '../shared/PhotoLightbox'

/**
 * Swipeable photos for the item in hand.
 *
 * Used equipment is identified by eye, not by name: two Strats with the same
 * title are told apart by the scuff on the scratchplate. The operator needs to
 * see the thing they are holding before committing it to a tote.
 *
 * Native horizontal scroll with snap points, rather than a carousel library:
 * the browser already does momentum, rubber-banding and touch handling
 * properly on a handheld, and none of it needs JavaScript to work.
 *
 * Tapping a photo opens it full screen in the app's existing lightbox, which
 * already has the close button, the arrows and the Escape handling. A second
 * implementation would only be a second thing to keep in step.
 */
const PHOTO_HEIGHT = 150

export default function PdaPhotos({ item }) {
    const images = itemImages(item)
    const [shown, setShown] = useState(0)
    // Index being viewed full screen, or null when the lightbox is closed.
    const [enlarged, setEnlarged] = useState(null)

    if (!images.length) return null

    function onScroll(event) {
        const { scrollLeft, clientWidth } = event.currentTarget
        if (!clientWidth) return
        setShown(Math.round(scrollLeft / clientWidth))
    }

    return (
        <div className="pda-photos">
            <div className="pda-photos-track" onScroll={onScroll}>
                {images.map((src, position) => (
                    <img
                        key={src}
                        src={src}
                        alt={`Photo ${position + 1} of ${images.length}`}
                        height={PHOTO_HEIGHT}
                        loading={position === 0 ? 'eager' : 'lazy'}
                        onClick={() => setEnlarged(position)}
                    />
                ))}
            </div>
            {images.length > 1 && (
                <div className="pda-photos-count">{shown + 1} / {images.length}</div>
            )}

            {enlarged !== null && (
                <PhotoLightbox
                    images={images}
                    initialIndex={enlarged}
                    onClose={() => setEnlarged(null)}
                />
            )}
        </div>
    )
}

/**
 * Every photo on an item, in the order Prism holds them, which is the order
 * they were arranged for the listing: lead shot first.
 *
 * Shared with the row list so the thumbnail and the gallery can never disagree
 * about which picture belongs to an item. The endpoint has returned both an
 * `images` array and a single `image_url` over its life, so both are accepted.
 */
export function itemImages(item) {
    if (Array.isArray(item?.images)) return item.images.filter(Boolean)
    return item?.image_url ? [item.image_url] : []
}
